import {useEffect, useRef, useState} from 'react';
import {Box, Select, MenuItem, Typography, SelectChangeEvent} from '@mui/material';
import {EntityShift} from '../../../backend/interfaces/entity-shift';
import ShiftAllocationPreview from '../shift-allocation-preview/shift-allocation-preview';
import {CustomCheckbox} from '../../checkbox/checkbox';
import {ArrowDropDown} from '@mui/icons-material';
import {ShiftAllocation} from '../shifts-allocations-table/shifts-allocations-table';
import './shift-allocation-select-input.scss';

interface ShiftAllocationSelectInputProps {
  allocation: ShiftAllocation;
  shifts: EntityShift[];
  userId: string;
  cellWidth: number;
  onChange: (selected: string, toDelete: boolean) => void;
}

/**
 * Due to performance reasons, this component will render a mocked Select component while closed, so that it can be
 * rendered in the shift allocations screen where many selects are rendered at the same time. The UI will look exactly
 * as a customized MUI Select, but the actual MUI component will be controlled and rendered only when the user clicks
 * the mocked Select element.
 */
const ShiftAllocationSelectInput = ({
  allocation: {entityShiftIds, day},
  shifts,
  userId,
  cellWidth,
  onChange,
}: ShiftAllocationSelectInputProps) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [firstTime, setFirstTime] = useState(true);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (firstTime) {
      setCheckedIds([]);
      shifts.forEach((shift) => {
        if (entityShiftIds.includes(shift.id!)) {
          setCheckedIds((prev) => [...prev, shift.id!]);
        }
      });
      setFirstTime(false);
    }
  }, [shifts, entityShiftIds, firstTime]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: {value},
    } = event;
    setCheckedIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClose = () => {
    setOpened(false);
    checkedIds.forEach((shiftId) => {
      if (!entityShiftIds.includes(shiftId)) onChange(shiftId, false);
    });
    entityShiftIds.forEach((shiftId) => {
      if (!checkedIds.includes(shiftId)) onChange(shiftId, true);
    });
  };

  return (
    <Box className="shifts-allocation-select-input" ref={boxRef}>
      {opened ? (
        <Select
          multiple
          displayEmpty
          value={checkedIds}
          open={opened}
          onClose={handleClose}
          onChange={handleChange}
          key={`allocation_input_${userId}_${day}}`}
          style={{width: cellWidth + 'px'}}
          MenuProps={{
            PaperProps: {
              style: {
                padding: '0',
                marginTop: '2px',
                backgroundColor: 'var(--dcm-white-darker)',
                boxShadow: '0px 1px 2px 0px rgba(60, 64, 67, 0.3)',
              },
            },
          }}
          renderValue={(selected) => {
            return (
              <ShiftAllocationPreview
                day={day}
                userId={userId}
                edit
                shifts={
                  shifts.filter((shift) => {
                    return selected.includes(shift.id!);
                  }) || []
                }
              />
            );
          }}
        >
          {shifts.map((shift) => {
            return (
              <MenuItem
                className="shifts-allocation-select-input-fields"
                key={shift.id}
                value={shift.id}
              >
                <CustomCheckbox checked={checkedIds.includes(shift.id!)} />
                <Typography noWrap>{`${shift.abbreviation}(${shift.name})`}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <Box
          style={{width: cellWidth + 'px'}}
          className="shifts-allocation-select-input-closed"
          onClick={() => setOpened(true)}
        >
          <ShiftAllocationPreview
            edit
            day={day}
            userId={userId}
            shifts={
              shifts.filter((shift) => {
                return checkedIds.includes(shift.id!);
              }) || []
            }
          />
          <ArrowDropDown sx={{color: 'var(--dcm-gray)'}} />
        </Box>
      )}
    </Box>
  );
};

export default ShiftAllocationSelectInput;
